import { AlertTriangle, ExternalLink } from 'lucide-react';
import { assertNever, PortalEntryRunStatus, type PortalRunDto } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { isPortalRunActive, useCancelPortalRun, usePortalRun, useStartPortalRun } from '@/hooks/queries';
import { answered, reportsUnreadyMessage } from '@/lib/reportsRead';

interface PortalRunPanelProps {
  reportId: string;
  concurrencyVersion: number;
  portalName: string;
  /** The gift, loan or spending whose 72/24-hour notice to run instead of the whole report. */
  noticeOf?: string | null;
}

/**
 * A portal-entry run (ADR-093): SignalVote uploads the report's files to the state portal after the
 * treasurer logs in through the live view, then shows what the portal holds and what is left to enter.
 * The treasurer still enters the form-only items and files there. A notice run uploads only the rows
 * one 72/24-hour notice lists; the treasurer certifies and files the notice in the portal.
 */
export function PortalRunPanel({ reportId, concurrencyVersion, portalName, noticeOf = null }: PortalRunPanelProps) {
  const runRead = usePortalRun(reportId, true, noticeOf);
  const run = answered(runRead);
  const { startRun, isStarting, startError } = useStartPortalRun();
  const { cancelRun, isCancelling, cancelError } = useCancelPortalRun();
  const error = startError ?? cancelError;
  if (runRead.status === 'loading') return null;
  const unready = reportsUnreadyMessage('portal run', runRead);

  const active = isPortalRunActive(run);
  return (
    <section className="space-y-3 rounded-lg border p-4" data-testid="portal-run-panel">
      {noticeOf ? (
        <div>
          <h4 className="font-medium">Let SignalVote upload this 72/24-hour notice to {portalName}</h4>
          <p className="text-sm text-muted-foreground">
            You log in to {portalName}. SignalVote uploads only the gifts or spending this notice lists, with the
            72/24-HR box checked, and skips any {portalName} already holds. Enter any loan it lists first. Then you
            certify with your PIN and file the notice on {portalName}’s e-File 72/24-HR Report page.
          </p>
        </div>
      ) : (
        <div>
          <h4 className="font-medium">Let SignalVote enter it in {portalName}</h4>
          <p className="text-sm text-muted-foreground">
            You log in to {portalName}. SignalVote uploads your files and checks {portalName} against your records. You
            enter any loans first, then the items it lists for you, then file and certify. 72/24-hour notices are filed on{' '}
            {portalName}’s 72/24-HR page.
          </p>
        </div>
      )}

      {run && <RunState run={run} portalName={portalName} />}
      {unready && (
        <p role="status" className="text-sm text-muted-foreground" data-testid="portal-run-unready">
          {unready}
        </p>
      )}

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error.message}
        </p>
      )}

      {active ? (
        <Button variant="outline" size="sm" disabled={isCancelling} onClick={() => cancelRun(run!)}>
          Cancel the run
        </Button>
      ) : (
        <Button size="sm" disabled={isStarting} onClick={() => startRun({ reportId, concurrencyVersion, noticeOf })}>
          {startLabel(portalName, noticeOf !== null, run != null)}
        </Button>
      )}
    </section>
  );
}

function startLabel(portalName: string, notice: boolean, again: boolean): string {
  const label = notice ? `Upload this notice to ${portalName}` : `Enter this report in ${portalName}`;
  return again ? `${label} again` : label;
}

function RunState({ run, portalName }: { run: PortalRunDto; portalName: string }) {
  switch (run.status) {
    case PortalEntryRunStatus.Starting:
    case PortalEntryRunStatus.WaitingForLogin:
      return run.liveViewUrl ? (
        <LiveView url={run.liveViewUrl} portalName={portalName} />
      ) : (
        <p className="text-sm text-muted-foreground">Opening {portalName}…</p>
      );
    case PortalEntryRunStatus.NeedsHuman:
      return (
        <div className="space-y-3">
          <Notice message={run.message ?? `The run needs you in ${portalName}.`} />
          {run.liveViewUrl && <LiveView url={run.liveViewUrl} portalName={portalName} />}
        </div>
      );
    case PortalEntryRunStatus.Running:
      return (
        <div className="space-y-2">
          <ul className="space-y-1 text-sm">
            {run.tasks.map((task) => (
              <li key={task.key} className="flex justify-between gap-4">
                <span>{task.label}</span>
                <span className="tabular-nums text-muted-foreground">
                  {task.completed} of {task.total}
                </span>
              </li>
            ))}
          </ul>
          {run.watchUrl && (
            <a href={run.watchUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm underline">
              Watch the run <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      );
    case PortalEntryRunStatus.Completed:
      return <Review run={run} portalName={portalName} />;
    case PortalEntryRunStatus.Failed:
      return <Notice message={run.message ?? 'The run stopped without saying why.'} />;
    case PortalEntryRunStatus.Cancelled:
      return <p className="text-sm text-muted-foreground">{run.message}</p>;
    default:
      return assertNever(run.status);
  }
}

function LiveView({ url, portalName }: { url: string; portalName: string }) {
  return (
    <div className="space-y-2">
      <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-medium underline">
        Log in to {portalName} <ExternalLink className="h-3 w-3" />
      </a>
      <iframe src={url} title={`${portalName} live view`} className="h-96 w-full rounded-md border" />
    </div>
  );
}

function Notice({ message }: { message: string }) {
  return (
    <div role="alert" className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <p>{message}</p>
    </div>
  );
}

function Review({ run, portalName }: { run: PortalRunDto; portalName: string }) {
  return (
    <div className="space-y-3 text-sm">
      {run.message && <p className="font-medium">{run.message}</p>}
      {run.review && <p>{`${run.review.matched} matched in ${portalName}`}</p>}
      {run.left.length > 0 && (
        <div>
          <p className="font-medium">Left for you to enter</p>
          <ul className="list-disc pl-5">
            {run.left.map((entry, i) => (
              <li key={`${entry.ledgerId ?? entry.kind}-${i}`}>{entry.reason}</li>
            ))}
          </ul>
        </div>
      )}
      {run.unrecorded.length > 0 && (
        <div>
          <p className="font-medium">Not found in {portalName}, so not marked reported</p>
          <ul className="list-disc pl-5">
            {run.unrecorded.map((miss) => (
              <li key={miss.ledgerId}>{miss.reason}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
